import { useState, useEffect } from "react";
import { ChevronDown, ChevronUp } from "lucide-react";
import Input from "../ui/Input";
import {
    useUpdatePermission,
    useCreatePermission,
    useGetPermissions,
    useTotalStaff,
} from "../Use-auth";
import SucessToast from "./SucessToast";

/* ---------------- PERMISSIONS DATA ---------------- */
const permissionsData = [
    { label: "Home", key: "home", children: [{ label: "Home View", key: "home_view" }] },
    {
        label: "Attendance",
        key: "attendance",
        children: [
            { label: "Attendance", key: "attendance_view" },
            {
                label: "Leave", key: "leave_view",
            },

        ],
    },
    {
        label: "Project",
        key: "project",
        children: [
            { label: "View", key: "project_view" },
            { label: "Add New Project", key: "project_new" },
        ],
    },
    {
        label: "Sales",
        key: "sales",
        children: [
            { label: "Sales View", key: "sales_view" },
        ],
    },
    // {
    //     label: "Account",
    //     key: "account",
    //     children: [
    //         { label: "Create New Account", key: "account_new" },
    //         { label: "Show Employees Data", key: "account_update" },
    //     ],
    // },
    {
        label: "Category",
        key: "category",
        children: [
            { label: "Category View", key: "category_view" },
            { label: "Update Category", key: "category_update" },
        ],
    },
    {
        label: "Management Edit Employees Permissions",
        key: "management",
        children: [
            { label: "Management Edit Permissions", key: "management_edit" },
        ],
    },

];

/* ---------------- HELPER ---------------- */
const countCheckedChildren = (permission, selected) => {
    if (!permission.children) return 0;

    return permission.children.reduce((sum, child) => {
        if (child.children) {
            return sum + countCheckedChildren(child, selected);
        }
        return sum + (selected[child.key] ? 1 : 0);
    }, 0);
};

/* ---------------- PERMISSION ITEM ---------------- */
function PermissionItem({ permission, selected, toggle, search, expandedAll, indeterminate }) {
    const hasChildren = permission.children?.length > 0;
    const isChecked = !!selected[permission.key];
    const [open, setOpen] = useState(false);

    useEffect(() => {
        setOpen(expandedAll);
    }, [expandedAll]);

    const matches =
        permission.label.toLowerCase().includes(search.toLowerCase()) ||
        permission.children?.some((c) =>
            c.label.toLowerCase().includes(search.toLowerCase())
        );

    if (!matches) return null;

    const totalChildren = permission.children?.length || 0;
    const checkedChildren = countCheckedChildren(permission, selected);

    return (
        <div className="ml-2 sm:ml-4 mb-2">
            <div
                className="flex justify-between items-center border rounded px-3 py-2 cursor-pointer"
                onClick={() => setOpen((p) => !p)}
            >
                <label className="flex gap-2 items-center text-[11px] zs:text-sm">
                    <input
                        type="checkbox"
                        checked={isChecked}
                        ref={el => {
                            if (el) el.indeterminate = indeterminate?.[permission.key] || false;
                        }}
                        onChange={(e) => {
                            e.stopPropagation();
                            toggle(permission.key, permission.children || []);
                        }}
                    />
                    {permission.label}
                </label>

                {hasChildren && (
                    <div className="flex items-center gap-3 text-gray-500">
                        <span className="text-[10px] zs:text-[12px]">{checkedChildren}/{totalChildren}</span>
                        {open ? <ChevronUp /> : <ChevronDown />}
                    </div>
                )}
            </div>

            {hasChildren && open && (
                <div className="mt-1">
                    {permission.children.map((child) => (
                        <PermissionItem
                            key={child.key}
                            permission={child}
                            selected={selected}
                            toggle={toggle}
                            search={search}
                            expandedAll={expandedAll}
                            indeterminate={indeterminate}
                        />
                    ))}
                </div>
            )}
        </div>
    );
}

/* ---------------- MAIN COMPONENT ---------------- */
export default function ManagementPermissions({ adminId }) {
    const [selected, setSelected] = useState({});
    const [search, setSearch] = useState("");
    const [expandedAll, setExpandedAll] = useState(false);
    const [selectedManagementId, setSelectedManagementId] = useState([]);
    const [saving, setSaving] = useState(false);
    const { data: staffData = [] } = useTotalStaff();
    const { data: permissionsDataApi = [], refetch } = useGetPermissions();
    const createPermission = useCreatePermission();
    const updatePermission = useUpdatePermission();


    const managementUsers = (staffData || [])
        .filter(emp => emp?.departmentName === "Management")
        .map(emp => ({
            userId: emp.employeeId,
            fullName: `${emp.firstName || ""} ${emp.lastName || ""}`.trim(),
            username: emp.officeEmail || "",
            department: "Management",
            status: emp.employeeStatus || "",
        }));

    const [toast, setToast] = useState({ show: false, message: "", type: "success" });

    useEffect(() => {
        if (!selectedManagementId.length) return;

        const next = {};

        selectedManagementId.forEach((userId) => {
            const existing = permissionsDataApi.find(p => p.userId === userId);
            next[userId] = existing?.management || {};
        });

        setSelected(next);
    }, [selectedManagementId]);

    /* -------- TOGGLE INDIVIDUAL PERMISSIONS -------- */
    const togglePermissionForUser = (userId, key, children = []) => {
        setSelected(prev => {
            const userPerm = { ...(prev[userId] || {}) };
            const value = !userPerm[key];
            userPerm[key] = value;

            const toggleChildren = list => {
                list.forEach(c => {
                    userPerm[c.key] = value;
                    if (c.children) toggleChildren(c.children);
                });
            };

            if (children.length) toggleChildren(children);

            return { ...prev, [userId]: userPerm };
        });
    };

    /* -------- SELECT ALL PERMISSIONS -------- */
    const toggleSelectAll = () => {
        const keys = [];
        const collectKeys = (list) => list.forEach((item) => {
            keys.push(item.key);
            if (item.children) collectKeys(item.children);
        });
        collectKeys(permissionsData);

        selectedManagementId.forEach(userId => {
            const allChecked = keys.every(k => selected[userId]?.[k]);
            const nextUserPerm = {};
            keys.forEach(k => (nextUserPerm[k] = !allChecked));
            selected[userId] = nextUserPerm;
        });

        setSelected({ ...selected });
    };

    /* -------- MERGE PERMISSIONS & INDTERMINATE -------- */
    const getMergedSelected = () => {
        const merged = {};
        const indeterminate = {};

        const mergePerm = (perm) => {
            if (!selectedManagementId.length) return;

            let allChecked = true;
            let someChecked = false;

            selectedManagementId.forEach((userId) => {
                const userPerm = selected[userId] || {};
                if (userPerm[perm.key]) someChecked = true;
                else allChecked = false;
            });

            merged[perm.key] = allChecked;
            indeterminate[perm.key] = someChecked && !allChecked;

            if (perm.children) perm.children.forEach(c => mergePerm(c));
        };

        permissionsData.forEach(perm => mergePerm(perm));

        return { merged, indeterminate };
    };

    const { merged: mergedSelected, indeterminate } = getMergedSelected();

    /* -------- SAVE PERMISSIONS -------- */

    const handleSave = async () => {
        if (!selectedManagementId.length) {
            setToast({
                show: true,
                message: "Select at least one management user first",
                type: "error",
            });
            return;
        }

        try {
            setSaving(true);

            const promises = selectedManagementId.map((userId) => {
                const userPerms = selected[userId] || {};
                const userDept = managementUsers.find(u => u.userId === userId)?.department;

                const managementPermissions = {};
                Object.keys(userPerms).forEach((key) => {
                    if (userPerms[key]) managementPermissions[key] = true;
                });

                const existing = permissionsDataApi.find((p) => p.userId === userId);

                if (existing) {
                    return updatePermission.mutateAsync({
                        id: existing._id,
                        management: managementPermissions,
                        employees: {},
                    });
                } else {
                    return createPermission.mutateAsync({
                        adminBy: adminId,
                        userId,
                        department: userDept,
                        management: managementPermissions,
                        employees: {},
                    });
                }
            });

            await Promise.all(promises);

            setToast({
                show: true,
                message: "Permissions saved successfully",
                type: "success",
            });

            refetch();

        } catch (err) {
            setToast({
                show: true,
                message: err.message || "Failed to save permissions",
                type: "error",
            });
        } finally {
            setSaving(false);
        }
    };

    return (
        <>
            <div className="mb-6 font-sans">
                {/* Header */}
                <label className="font-semibold text-[14px] sm:text-[18px] block mb-3 text-gray-800">
                    Select Management User(s)
                </label>

                {/* Container */}
                <div className="border border-gray-200 rounded-xl shadow-md p-2 sm:p-5 bg-white">

                    {/* Select All Users */}
                    <label className="flex items-center gap-3 mb-4 px-4 py-2 rounded-lg border border-gray-300 cursor-pointer hover:bg-gray-50 transition">
                        <input
                            type="checkbox"
                            className="w-4 h-4 text-blue-600 border-gray-300 rounded "
                            checked={selectedManagementId.length === managementUsers.length}
                            onChange={(e) => {
                                setSelectedManagementId(
                                    e.target.checked ? managementUsers.map(u => u.userId) : []
                                );
                            }}
                        />
                        <span className="font-medium text-gray-700 text-[11px] sm:text-sm text-xs">Select All Users</span>
                    </label>
                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 max-h-72 overflow-y-auto">
                        {managementUsers.map((u) => (
                            <label
                                key={u.userId}
                                className="flex items-center gap-3 px-4 py-3 border border-gray-300 rounded-lg cursor-pointer hover:shadow-md hover:bg-gray-50 transition"
                            >
                                <input
                                    type="checkbox"
                                    className="w-4 h-4 text-blue-600 border-gray-300 rounded "
                                    checked={selectedManagementId.includes(u.userId)}
                                    onChange={(e) => {
                                        const next = e.target.checked
                                            ? [...selectedManagementId, u.userId]
                                            : selectedManagementId.filter(id => id !== u.userId);
                                        setSelectedManagementId(next);
                                    }}
                                />
                                <div className="flex flex-col">
                                    <span className="font-medium text-gray-800 text-[13px] sm:text-[16px]">{u.fullName}</span>
                                    <span className="text-[11px] sm:text-sm text-gray-500">({u.username})</span>
                                </div>
                            </label>
                        ))}
                    </div>
                </div>
            </div>

            {/* ---------------- Permissions Section ---------------- */}
            {selectedManagementId.length === 0 ? (
                <div className=" p-2 sm:p-4 border rounded-lg bg-gray-100 text-sm text-gray-700">
                    Select at least one management user to assign permissions
                </div>
            ) : (
                <div className=" p-2 sm:p-4 border rounded-lg bg-gray-50">
                    {/* Search Input */}
                    <Input
                        placeholder="Search permissions..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        className="mb-4 w-full border-gray-300 focus:ring-blue-500 focus:border-blue-500"
                    />

                    {/* Header: Select All + Actions */}
                    <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-3 my-4">
                        {/* Select All Permissions */}
                        <label className="flex items-center gap-2 text-gray-700 text-[12px] sm:text-[14px]">
                            <input
                                type="checkbox"
                                checked={Object.keys(mergedSelected).length > 0 && Object.values(mergedSelected).every(Boolean)}
                                ref={el => {
                                    if (el) {
                                        const someChecked = Object.values(mergedSelected).some(v => v);
                                        const allChecked = Object.values(mergedSelected).every(v => v);
                                        el.indeterminate = someChecked && !allChecked;
                                    }
                                }}
                                onChange={toggleSelectAll}
                                className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                            />
                            Select All Permissions
                        </label>

                        {/* Action Buttons */}
                        <div className="flex gap-2 mt-2 md:mt-0">
                            <button
                                onClick={handleSave}
                                disabled={saving}
                                className={`px-3 py-1 text-[11px] zs:text-sm rounded-lg border border-green-600 transition flex items-center gap-2
        ${saving
                                        ? "bg-green-200 text-green-700 opacity-70 cursor-not-allowed"
                                        : "bg-green-100 text-green-700 hover:bg-green-200"
                                    }`}
                            >
                                {saving ? (
                                    <>
                                        Saving...
                                        <span className="loader-circle"></span>
                                    </>
                                ) : (
                                    "Save"
                                )}
                            </button>
                            <button
                                onClick={() => setExpandedAll(p => !p)}
                                className="px-3 py-1 text-[11px] zs:text-sm rounded-lg bg-blue-100 text-blue-700 border border-blue-600 hover:bg-blue-200 transition"
                            >
                                {expandedAll ? "Collapse All" : "Expand All"}
                            </button>
                        </div>
                    </div>

                    {/* Permissions per Management User */}
                    <div className="flex flex-col gap-4">
                        {selectedManagementId.map((userId) => {
                            const currentUser = managementUsers.find(u => u.userId === userId);

                            return (
                                <div key={userId} className="p-1 sm:p-4 border rounded-lg bg-white shadow-sm">
                                    {/* User Name */}
                                    <div className="font-bold text-gray-800 mb-3 text-[11px] zs:text-sm">
                                        {currentUser?.fullName}
                                    </div>

                                    {/* Permission Items */}
                                    <div className="flex flex-col gap-2 text-[11px] zs:text-sm">
                                        {permissionsData.map((perm) => (
                                            <PermissionItem
                                                key={perm.key}
                                                permission={perm}
                                                selected={selected[userId] || {}}
                                                toggle={(key, children) =>
                                                    togglePermissionForUser(userId, key, children)
                                                }
                                                search={search}
                                                expandedAll={expandedAll}
                                                indeterminate={{}}
                                            />
                                        ))}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}

            {toast.show && (
                <SucessToast
                    message={toast.message}
                    type={toast.type}
                    onClose={() => setToast({ ...toast, show: false })}
                />
            )}
        </>
    );
}
